'use client'

import { AnimatePresence, motion } from 'framer-motion'
import { useNavigationStore } from '@/store/use-navigation-store'
import { useComparisonStore } from '@/store/use-comparison-store'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { X, GitCompareArrows } from 'lucide-react'
import dynamic from 'next/dynamic'
import { FlyingCartItem } from '@/components/layout/FlyingCartItem'

// Dynamic imports for all page components to avoid SSR issues
const HomePage = dynamic(() => import('@/components/pages/HomePage'), { ssr: false })
const CatalogPage = dynamic(() => import('@/components/pages/CatalogPage'), { ssr: false })
const ProductDetailPage = dynamic(() => import('@/components/pages/ProductDetailPage'), { ssr: false })
const CartPage = dynamic(() => import('@/components/pages/CartPage'), { ssr: false })
const CheckoutPage = dynamic(() => import('@/components/pages/CheckoutPage'), { ssr: false })
const CheckoutSuccessPage = dynamic(() => import('@/components/pages/CheckoutSuccessPage'), { ssr: false })
const ComparisonPage = dynamic(() => import('@/components/pages/ComparisonPage'), { ssr: false })
const AdminPage = dynamic(() => import('@/components/pages/AdminPage'), { ssr: false })
const ManufacturersPage = dynamic(() => import('@/components/pages/ManufacturersPage'), { ssr: false })
const DeliveryPage = dynamic(() => import('@/components/pages/DeliveryPage'), { ssr: false })
const AboutPage = dynamic(() => import('@/components/pages/AboutPage'), { ssr: false })
const ContactsPage = dynamic(() => import('@/components/pages/ContactsPage'), { ssr: false })
const WishlistPage = dynamic(() => import('@/components/pages/WishlistPage'), { ssr: false })
const OrderTrackingPage = dynamic(() => import('@/components/pages/OrderTrackingPage'), { ssr: false })
const FaqPage = dynamic(() => import('@/components/pages/FaqPage'), { ssr: false })
const ProfilePage = dynamic(() => import('@/components/pages/ProfilePage'), { ssr: false })
const RecentlyViewedPage = dynamic(() => import('@/components/pages/RecentlyViewedPage'), { ssr: false })

const pageVariants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
}

function ComparisonBar() {
  const productIds = useComparisonStore((s) => s.productIds)
  const removeProduct = useComparisonStore((s) => s.removeProduct)
  const clearComparison = useComparisonStore((s) => s.clearComparison)
  const navigate = useNavigationStore((s) => s.navigate)

  if (productIds.length === 0) return null

  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      className="fixed bottom-20 left-1/2 z-30 -translate-x-1/2"
    >
      <div className="flex items-center gap-2 rounded-full border bg-background/95 px-4 py-2 shadow-lg backdrop-blur sm:gap-3 sm:px-5 sm:py-3">
        <GitCompareArrows className="h-4 w-4 text-muted-foreground sm:h-5 sm:w-5" />
        <span className="text-sm font-medium sm:text-base">
          Сравнение: <Badge variant="secondary">{productIds.length}</Badge>
        </span>
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs sm:h-8 sm:text-sm"
          onClick={() => navigate('comparison')}
        >
          Открыть
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 w-7 p-0 sm:h-8 sm:w-8"
          onClick={clearComparison}
        >
          <X className="h-3 w-3 sm:h-4 sm:w-4" />
        </Button>
      </div>
    </motion.div>
  )
}

function PageRouter() {
  const page = useNavigationStore((s) => s.page)

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={page}
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.2 }}
      >
        {page === 'home' && <HomePage />}
        {page === 'catalog' && <CatalogPage />}
        {page === 'product' && <ProductDetailPage />}
        {page === 'cart-full' && <CartPage />}
        {page === 'checkout' && <CheckoutPage />}
        {page === 'checkout-success' && <CheckoutSuccessPage />}
        {page === 'comparison' && <ComparisonPage />}
        {page === 'admin' && <AdminPage />}
        {page === 'manufacturers' && <ManufacturersPage />}
        {page === 'delivery' && <DeliveryPage />}
        {page === 'about' && <AboutPage />}
        {page === 'contacts' && <ContactsPage />}
        {page === 'wishlist' && <WishlistPage />}
        {page === 'order-tracking' && <OrderTrackingPage />}
        {page === 'faq' && <FaqPage />}
        {page === 'profile' && <ProfilePage />}
        {page === 'recently-viewed' && <RecentlyViewedPage />}
      </motion.div>
    </AnimatePresence>
  )
}

export default function Home() {
  return (
    <>
      <FlyingCartItem />
      <PageRouter />
      <AnimatePresence>
        <ComparisonBar />
      </AnimatePresence>
    </>
  )
}
